import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabaseClient';

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let mounted = true;
    try {
      const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
      const params = new URLSearchParams(hash);
      const access_token = params.get('access_token');
      const refresh_token = params.get('refresh_token');
      const token_type = params.get('token_type') || 'bearer';
      const expires_in = params.get('expires_in');
      if (access_token && refresh_token) {
        (supabase.auth as any).setSession?.({ access_token, refresh_token, token_type, expires_in: Number(expires_in || 3600) });
      }
    } catch {}

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setReady(Boolean(data?.session));
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      setReady(Boolean(session));
    });
    return () => { mounted = false; sub.subscription.unsubscribe(); };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirm) { setError(t('auth.changeMismatch')); return; }
    if (password.length < 6) { setError(t('auth.changeTooShort')); return; }
    setSubmitting(true); setMessage(null); setError(null);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      navigate('/', { replace: true });
      return;
    } catch (err: any) {
      const msg = (err && err.message) ? `${t('auth.changeError')}: ${err.message}` : t('auth.changeError');
      setError(msg);
      try { console.error('[auth] change password error', err); } catch {}
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-gray-900 rounded-lg border border-gray-800 p-6">
        <h1 className="text-xl font-semibold mb-2">{t('auth.changeTitle')}</h1>
        {!ready ? (
          <p className="text-sm text-gray-400">{t('auth.changeNotReady', 'The link is not active. Please come from the email link.')}</p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-sm text-gray-300">{t('auth.newPassword')}</label>
              <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required className="w-full rounded-md bg-black/40 border border-gray-700 px-3 py-2 text-white" />
            </div>
            <div>
              <label className="block text-sm text-gray-300">{t('auth.confirmPassword')}</label>
              <input value={confirm} onChange={(e) => setConfirm(e.target.value)} type="password" required className="w-full rounded-md bg-black/40 border border-gray-700 px-3 py-2 text-white" />
            </div>
            <button disabled={submitting} className="w-full bg-pink-600 hover:bg-pink-700 disabled:opacity-50 text-white font-semibold rounded-md py-2 mt-2">{t('auth.changeSubmit')}</button>
          </form>
        )}
        {message && <p className="text-sm text-green-400 mt-3">{message}</p>}
        {error && <p className="text-sm text-red-400 mt-3">{error}</p>}
      </div>
    </div>
  );
}


